import { getLocale, request } from '@umijs/max';

const SUCCESS_CODE = 200;

/** 视频生成 - 参考模式 */
export const AI_SCENE_VIDEO_REF = 1;
/** 视频生成 - 首尾帧模式 */
export const AI_SCENE_VIDEO_FIRST_LAST = 2;
/** 图片生成 */
export const AI_SCENE_IMAGE = 3;

export type ResultResponse<T> = {
  code: number;
  message?: string;
  data?: T;
};

export type AiBusinessModel = {
  id: number;
  modelName: string;
  modelDesc: string;
  modelCost: string;
  modelUsage: string;
};

export type AiBusinessModelComponentParameter = {
  lanage?: number;
  componentName: string;
  componentKey: string;
  componentType: number;
  componentDefaultValue?: string;
  componentMinLimit?: number;
  componentMaxLimit?: number;
  componentRule?: string;
  componentPreset?: string;
  componentSort?: number;
  isPublic?: number;
  componentTips?: string;
};

function assertSuccess<T>(res: ResultResponse<T> | undefined, fallback: string): T {
  if (res?.code === SUCCESS_CODE && res.data !== undefined) {
    return res.data;
  }
  throw new Error(res?.message?.trim() || fallback);
}

function wrapRequestError(error: unknown, fallback: string): never {
  const data = (error as { response?: { data?: ResultResponse<unknown> } })?.response?.data;
  if (data?.message) {
    throw new Error(data.message);
  }
  if (error instanceof Error && error.message) {
    throw error;
  }
  throw new Error(fallback);
}

/** 按业务场景查询模型列表 GET /app/ai/business/model/search/{sceneId} */
export async function findScene(sceneId: number = AI_SCENE_VIDEO_REF): Promise<AiBusinessModel[]> {
  try {
    const res = await request<ResultResponse<AiBusinessModel[]>>(
      `/app/ai/business/model/search/${sceneId}`,
      {
        method: 'GET',
        skipErrorHandler: true,
      },
    );
    return assertSuccess(res, '模型列表加载失败') ?? [];
  } catch (error: unknown) {
    wrapRequestError(error, '模型列表加载失败');
  }
}

export type SubmitBusinessModelCellParams = {
  workspaceId: number;
  businessModelId: number;
  businessParams: string;
  /** 生成数量 */
  generationNumber: number;
};

/** AI 调用提交 POST /app/ai/business/model/cell */
export async function submitBusinessModelCell(
  params: SubmitBusinessModelCellParams,
): Promise<string> {
  try {
    const res = await request<ResultResponse<string>>('/app/ai/business/model/cell', {
      method: 'POST',
      data: {
        workspaceId: params.workspaceId,
        businessModelId: params.businessModelId,
        businessParams: params.businessParams,
        generationNumber: params.generationNumber,
      },
      skipErrorHandler: true,
    });
    if (res?.code !== SUCCESS_CODE) {
      throw new Error(res?.message?.trim() || '提交失败');
    }
    return res.data ?? '';
  } catch (error: unknown) {
    wrapRequestError(error, '提交失败');
  }
}

/** 按模型 ID 查询组件参数 GET /app/ai/business/model/{modelId} */
export async function findModel(modelId: number): Promise<AiBusinessModelComponentParameter[]> {
  try {
    const res = await request<ResultResponse<AiBusinessModelComponentParameter[]>>(
      `/app/ai/business/model/${modelId}`,
      {
        method: 'GET',
        headers: {
          'Accept-Language': getLocale(),
        },
        skipErrorHandler: true,
      },
    );
    return assertSuccess(res, '模型参数加载失败') ?? [];
  } catch (error: unknown) {
    wrapRequestError(error, '模型参数加载失败');
  }
}
